import { createContext, useContext, useState, useEffect } from 'react'

const ComparisonContext = createContext()

const STORAGE_KEY = 'powertron_comparison_items'
const ROI_STORAGE_KEY = 'powertron_roi_scenarios'
const MAX_ITEMS = 3

export function ComparisonProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  })

  const [roiScenarios, setRoiScenarios] = useState(() => {
    try {
      const stored = localStorage.getItem(ROI_STORAGE_KEY)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  })

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  }, [items])

  useEffect(() => {
    localStorage.setItem(ROI_STORAGE_KEY, JSON.stringify(roiScenarios))
  }, [roiScenarios])

  const addROIScenario = (scenario) => {
    setRoiScenarios(prev => {
      const exists = prev.some(s => s.id === scenario.id)
      if (exists) return prev

      const newScenarios = [...prev, scenario]
      if (newScenarios.length > MAX_ITEMS) {
        return newScenarios.slice(-MAX_ITEMS)
      }
      return newScenarios
    })
  }

  const removeROIScenario = (id) => {
    setRoiScenarios(prev => prev.filter(s => s.id !== id))
  }

  const clearROIScenarios = () => {
    setRoiScenarios([])
  }

  const addItem = (item) => {
    setItems(prev => {
      // Check if already exists
      const exists = prev.some(i => i.id === item.id && i.type === item.type)
      if (exists) return prev

      // Limit to MAX_ITEMS
      const newItems = [...prev, item]
      if (newItems.length > MAX_ITEMS) {
        return newItems.slice(-MAX_ITEMS)
      }
      return newItems
    })
  }

  const removeItem = (id, type) => {
    setItems(prev => prev.filter(i => !(i.id === id && i.type === type)))
  }

  const clearItems = () => {
    setItems([])
  }

  const hasItem = (id, type) => {
    return items.some(i => i.id === id && i.type === type)
  }

  return (
    <ComparisonContext.Provider value={{
      items,
      addItem,
      removeItem,
      clearItems,
      hasItem,
      itemCount: items.length,
      maxItems: MAX_ITEMS,
      // ROI Scenarios
      roiScenarios,
      addROIScenario,
      removeROIScenario,
      clearROIScenarios
    }}>
      {children}
    </ComparisonContext.Provider>
  )
}

export function useComparison() {
  const context = useContext(ComparisonContext)
  if (!context) {
    throw new Error('useComparison must be used within a ComparisonProvider')
  }
  return context
}

export default ComparisonContext
